import { useTranslation } from 'react-i18next';
import { api } from '@/store/api';
import { useAppSelector } from '@/store/hooks';
import { usePageMetadata } from '@/hooks/usePageMetadata';
import { Header } from '@/components/layout/Header';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Avatar,
  List,
  ListItem,
  ListItemText,
  Stack,
  CircularProgress,
} from '@gapartners/ui';

const { useGetManagerQuery, useGetDirectReportsQuery } = api;

export function TeamPage() {
  const { t } = useTranslation();

  // Set page metadata
  usePageMetadata({
    title: t('pages.team.title'),
    description: t('pages.team.description'),
  });

  const isManager = useAppSelector(
    state => state.auth.user?.isManager === true
  );

  const {
    data: manager,
    isLoading: isLoadingManager,
    error: managerError,
    refetch: refetchManager,
  } = useGetManagerQuery();

  const {
    data: directReports,
    isLoading: isLoadingDirectReports,
    error: directReportsError,
    refetch: refetchDirectReports,
  } = useGetDirectReportsQuery(undefined, {
    skip: !isManager,
  });
  const reports = directReports ?? [];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Header />
      <Paper
        variant="outlined"
        square
        sx={{
          px: 0,
          py: { xs: 1, sm: 1.25 },
          bgcolor: 'action.hover',
          borderBottom: '1px solid',
          borderColor: 'divider',
          borderLeft: 0,
          borderRight: 0,
          borderTop: 0,
          borderRadius: 0,
        }}
      >
        <Container maxWidth="xl">
          <Typography
            variant="h5"
            fontWeight={600}
            color="text.primary"
            sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, mb: 0.5 }}
          >
            {t('pages.team.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0 }}>
            {t('pages.team.description')}
          </Typography>
        </Container>
      </Paper>

      <Container
        maxWidth="xl"
        sx={{ py: { xs: 3, sm: 4 }, px: { xs: 2, sm: 3 } }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
            gap: { xs: 2, sm: 3 },
          }}
        >
          <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: { xs: 'stretch', sm: 'center' },
                justifyContent: 'space-between',
                gap: 2,
                mb: 2,
                flexWrap: 'wrap',
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight={600}
                color="text.primary"
              >
                {t('pages.team.manager')}
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => refetchManager()}
                disabled={isLoadingManager}
              >
                {isLoadingManager ? t('common.loading') : t('common.refresh')}
              </Button>
            </Box>

            {isLoadingManager && (
              <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress />
              </Box>
            )}

            {managerError && (
              <Box role="alert" sx={{ color: 'error.main', py: 2 }}>
                <Typography fontWeight={600}>{t('common.error')}</Typography>
                <Typography variant="body2">
                  {'status' in managerError
                    ? `${t('common.status')}: ${managerError.status}`
                    : t('pages.team.errorLoadingManager')}
                </Typography>
              </Box>
            )}

            {manager && !isLoadingManager && (
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ width: 64, height: 64 }}>
                    {manager.displayName?.charAt(0).toUpperCase() ||
                      manager.mail?.charAt(0).toUpperCase() ||
                      manager.userPrincipalName?.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      color="text.primary"
                    >
                      {manager.displayName}
                    </Typography>
                    {manager.mail && (
                      <Typography variant="body2" color="text.secondary">
                        {manager.mail}
                      </Typography>
                    )}
                    {!manager.mail && manager.userPrincipalName && (
                      <Typography variant="body2" color="text.secondary">
                        {manager.userPrincipalName}
                      </Typography>
                    )}
                    {manager.jobTitle && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        {manager.jobTitle}
                      </Typography>
                    )}
                  </Box>
                </Stack>
                {(manager.officeLocation ||
                  manager.mobilePhone ||
                  manager.businessPhones.length > 0) && (
                  <Box
                    sx={{
                      pt: 2,
                      borderTop: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {manager.officeLocation && (
                      <Typography variant="body2" color="text.secondary">
                        <Box component="span" fontWeight={600}>
                          {t('pages.team.location')}:
                        </Box>{' '}
                        {manager.officeLocation}
                      </Typography>
                    )}
                    {manager.mobilePhone && (
                      <Typography variant="body2" color="text.secondary">
                        <Box component="span" fontWeight={600}>
                          {t('pages.team.mobilePhone')}:
                        </Box>{' '}
                        {manager.mobilePhone}
                      </Typography>
                    )}
                    {manager.businessPhones.length > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        <Box component="span" fontWeight={600}>
                          {t('pages.team.businessPhone')}:
                        </Box>{' '}
                        {manager.businessPhones.join(', ')}
                      </Typography>
                    )}
                  </Box>
                )}
              </Stack>
            )}

            {!manager && !isLoadingManager && !managerError && (
              <Typography
                color="text.secondary"
                sx={{ textAlign: 'center', py: 4 }}
              >
                {t('pages.team.noManager')}
              </Typography>
            )}
          </Paper>

          <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: { xs: 'stretch', sm: 'center' },
                justifyContent: 'space-between',
                gap: 2,
                mb: 2,
                flexWrap: 'wrap',
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight={600}
                color="text.primary"
              >
                {t('pages.team.directReports')}
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => refetchDirectReports()}
                disabled={isLoadingDirectReports}
              >
                {t('common.refresh')}
              </Button>
            </Box>

            {isLoadingDirectReports && (
              <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress />
              </Box>
            )}

            {directReportsError && isManager && (
              <Box role="alert" sx={{ color: 'error.main', py: 2 }}>
                <Typography fontWeight={600}>{t('common.error')}</Typography>
                <Typography variant="body2">
                  {'status' in directReportsError
                    ? `${t('common.status')}: ${directReportsError.status}`
                    : t('pages.team.errorLoadingDirectReports')}
                </Typography>
              </Box>
            )}

            {!isManager || !isLoadingDirectReports ? (
              <Box>
                {!isManager || reports.length === 0 ? (
                  <Typography
                    color="text.secondary"
                    sx={{ textAlign: 'center', py: 4 }}
                  >
                    {t('pages.team.noDirectReports')}
                  </Typography>
                ) : (
                  <List
                    disablePadding
                    sx={{ borderRadius: 2, overflow: 'hidden' }}
                  >
                    {reports.map(report => {
                      const initials =
                        report.displayName?.charAt(0).toUpperCase() ||
                        report.mail?.charAt(0).toUpperCase() ||
                        report.userPrincipalName?.charAt(0).toUpperCase();
                      return (
                        <ListItem
                          key={report.id}
                          sx={{
                            px: { xs: 2, sm: 3 },
                            py: 1.5,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                            sx={{ minWidth: 0 }}
                          >
                            <Avatar sx={{ width: 48, height: 48 }}>
                              {initials}
                            </Avatar>
                            <ListItemText
                              primary={
                                <Typography
                                  fontWeight={600}
                                  color="text.primary"
                                >
                                  {report.displayName}
                                </Typography>
                              }
                              secondary={
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {report.mail || report.userPrincipalName}
                                </Typography>
                              }
                            />
                          </Stack>
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </Box>
            ) : null}
          </Paper>
        </Box>
      </Container>
    </Box>
  );
}
